/**
 * Pure per-customer economics model — the same math as the pricing one-pager.
 *
 * ONE PUBLISHED PRICE. $150 a month while payments run through Ruta Pay, $375
 * for a month without Ruta Pay volume — except a $0-revenue month for a company
 * with Ruta Pay history, which stays $150 (the first month is always $150; this
 * models a steady-state month). Texts are metered in billed texts
 * (ceil(SMS segments / 3) + MMS) at 5¢ each — the 200 starter texts are
 * one-time, so no monthly allowance. AI overage is not sold. Card cost is
 * JustiFi 2.35%; spread is the list rate minus that.
 *
 * Payments are priced by METHOD. Card is a rate + flat fee; bank (ACH) is a
 * pure rate with a per-transaction cap. Costs are BUILT IN, not levers.
 *
 * No React, no I/O — fully unit-testable.
 */

export interface ModelLevers {
  /** Monthly platform fee while payments run through Ruta Pay. */
  basePrice: number;
  /** Monthly platform fee for a month with no Ruta Pay (card or bank) volume. */
  priceWithoutPayments: number;
  /** Billed texts included each month. */
  includedTexts: number;
  /** AI credits bundled into the base price. */
  includedAi: number;
  /** Text-segment overage pack size. */
  topUpTexts: number;
  /** Price of one text-segment overage pack (USD). */
  topUpTextsPrice: number;
  /** AI-credit overage pack size. */
  topUpAiCredits: number;
  /** Price of one AI-credit overage pack (USD). */
  topUpAiCreditsPrice: number;

  /** Card rate charged to the payer, as a percent (e.g. 3.99). */
  cardRatePct: number;
  /** Flat per-transaction card fee charged to the payer, in dollars (e.g. 0.30). */
  cardTxnFee: number;
  /** Bank (ACH) rate charged to the payer, as a percent (e.g. 1). No flat fee. */
  achRatePct: number;
  /**
   * Cap on the total bank fee for a SINGLE transaction, in dollars (e.g. 25).
   * 0 (or negative) means uncapped. Card is never capped.
   */
  achFeeCap: number;

  /** Fixed platform overhead per month, split across the assumed paying customers. */
  platformFixedMonthly: number;
  /** Assumed paying-customer count the fixed overhead is split across. */
  assumedPayingCustomers: number;
  /** Marginal cost per AI credit. */
  aiUnitCost: number;
  /** Resolved from the provider toggle (Twilio | Telnyx) before calling. */
  smsUnitCost: number;
  mmsUnitCost: number;

  /**
   * What a CARD payment costs us, as a percent of volume — Quality Seasons'
   * actual net effective JustiFi rate.
   */
  cardCostPct: number;
  /** What a BANK (ACH) payment costs us — JustiFi's ACH rate (bps + flat). */
  achCostBps: number;
  achCostFixed: number;
}

export interface OrgUsage {
  /** Reference stat only — not priced, not costed. */
  laborHours: number;
  /** Reference stat only — not priced, not costed. */
  visitCount: number;
  smsSegments: number;
  mmsSegments: number;
  aiRequests: number;
  /** Dollars paid by card this month. */
  cardVolume: number;
  /** Number of card transactions this month. */
  cardTxns: number;
  /** Dollars paid by bank (ACH) this month. */
  achVolume: number;
  /** Number of bank (ACH) transactions this month. */
  achTxns: number;
  /**
   * Revenue the company generated this month (real orgs). Unset (scenario
   * profiles) counts as having revenue.
   */
  revenue?: number;
  /**
   * Has the company ever taken a Ruta Pay payment (real orgs, as far as the
   * window shows)? A $0-revenue month keeps $150 only with that history.
   */
  hasRutaPayHistory?: boolean;
}

/** One payment method's economics: what we charge, what it costs, what we keep. */
export interface MethodSpread {
  volume: number;
  txns: number;
  /** What the payer is charged in processing fees. */
  charge: number;
  /** What the processor takes from us. */
  fees: number;
  /** charge − fees. */
  spread: number;
}

export interface OrgEconomics {
  /** The monthly fee this month: `basePrice` with Ruta Pay volume, `priceWithoutPayments` without. */
  platformFee: number;
  /** This customer's share of fixed platform overhead (fixed ÷ assumed customers). */
  platformShare: number;
  /** Total cost to serve (platform share + metered text & AI cost). */
  cost: number;
  /** Billed texts = ceil(SMS segments / 3) + MMS. */
  billableTexts: number;
  /** Extra billed texts above the included allowance. */
  textPacks: number;
  /** AI overage packs billed above the included AI credits. */
  aiPacks: number;
  /** Charges for billed texts past the free ones (AI overage is not sold). */
  overage: number;
  /** Subscription revenue (this month's fee + texts). */
  subscription: number;
  /** (subscription − cost) / subscription. */
  subMargin: number;

  /** Card processing: charge, cost, spread. */
  card: MethodSpread;
  /** Bank (ACH) processing: charge, cost, spread — the fee is capped per txn. */
  ach: MethodSpread;
  /** Total charged to payers across both methods. */
  customerCharge: number;
  /** Total taken by processors across both methods. */
  processorFees: number;
  /** card.spread + ach.spread. */
  paymentSpread: number;

  /** Subscription + payment spread. */
  totalRev: number;
  /** (subscription − cost) + payment spread. */
  totalProfit: number;
  /** totalProfit / totalRev. */
  totalMargin: number;
}

function overagePacks(used: number, included: number, packSize: number): number {
  const over = Math.max(0, used - included);
  if (over <= 0) return 0;
  const size = packSize > 0 ? packSize : 1;
  return Math.ceil(over / size);
}

function methodSpread(opts: {
  volume: number;
  txns: number;
  ratePct: number;
  txnFee: number;
  feeCap: number;
  costBps: number;
  costFixed: number;
}): MethodSpread {
  const volume = Math.max(0, opts.volume);
  const txns = Math.max(0, opts.txns);
  let charge = volume * (opts.ratePct / 100) + txns * opts.txnFee;
  if (opts.feeCap > 0 && txns > 0) {
    // Cap applies per transaction: model as min(uncapped total, cap × txns).
    charge = Math.min(charge, opts.feeCap * txns);
  }
  const fees = volume * (opts.costBps / 10_000) + txns * opts.costFixed;
  return {
    volume,
    txns,
    charge,
    fees,
    spread: charge - fees,
  };
}

export function computeOrgEconomics(u: OrgUsage, L: ModelLevers): OrgEconomics {
  const platformShare = L.platformFixedMonthly / Math.max(1, L.assumedPayingCustomers);

  const cost =
    platformShare +
    u.smsSegments * L.smsUnitCost +
    u.mmsSegments * L.mmsUnitCost +
    u.aiRequests * L.aiUnitCost;

  // Admin scenario from segment inputs. Live customer billing folds per send
  // at increment time (RUT-3690) — do not treat this as the period meter.
  const billableTexts =
    Math.ceil(Math.max(0, u.smsSegments) / 3) + Math.max(0, u.mmsSegments);
  const packSize = L.topUpTexts > 0 ? L.topUpTexts : 1;
  const textPacks = overagePacks(billableTexts, L.includedTexts, packSize);
  // AI overage is not sold.
  const aiPacks = L.topUpAiCreditsPrice > 0
    ? overagePacks(u.aiRequests, L.includedAi, L.topUpAiCredits)
    : 0;
  const overage =
    textPacks * L.topUpTextsPrice + aiPacks * L.topUpAiCreditsPrice;

  // Any money through Ruta Pay this month keeps the lower price; so does a
  // $0-revenue month for a company with Ruta Pay history. $375 otherwise.
  const onRutaPay = u.cardVolume + u.achVolume > 0;
  const quietWithHistory =
    u.revenue !== undefined && u.revenue <= 0 && u.hasRutaPayHistory === true;
  const platformFee = Math.max(
    0,
    onRutaPay || quietWithHistory ? L.basePrice : L.priceWithoutPayments,
  );
  const subscription = platformFee + overage;
  const subMargin = subscription > 0 ? (subscription - cost) / subscription : 0;

  const card = methodSpread({
    volume: u.cardVolume,
    txns: u.cardTxns,
    ratePct: L.cardRatePct,
    txnFee: L.cardTxnFee,
    feeCap: 0,
    costBps: L.cardCostPct * 100,
    costFixed: 0,
  });
  const ach = methodSpread({
    volume: u.achVolume,
    txns: u.achTxns,
    ratePct: L.achRatePct,
    txnFee: 0,
    feeCap: L.achFeeCap,
    costBps: L.achCostBps,
    costFixed: L.achCostFixed,
  });

  const customerCharge = card.charge + ach.charge;
  const processorFees = card.fees + ach.fees;
  const paymentSpread = card.spread + ach.spread;

  const totalRev = subscription + paymentSpread;
  const totalProfit = subscription - cost + paymentSpread;
  const totalMargin = totalRev > 0 ? totalProfit / totalRev : 0;

  return {
    platformFee,
    platformShare,
    cost,
    billableTexts,
    textPacks,
    aiPacks,
    overage,
    subscription,
    subMargin,
    card,
    ach,
    customerCharge,
    processorFees,
    paymentSpread,
    totalRev,
    totalProfit,
    totalMargin,
  };
}

export function splitPaymentsByMethod(
  paymentVolume: number,
  paymentTxns: number,
  achSharePct: number,
): Pick<OrgUsage, 'cardVolume' | 'cardTxns' | 'achVolume' | 'achTxns'> {
  const share = Math.min(100, Math.max(0, achSharePct)) / 100;
  return {
    achVolume: paymentVolume * share,
    achTxns: paymentTxns * share,
    cardVolume: paymentVolume * (1 - share),
    cardTxns: paymentTxns * (1 - share),
  };
}

/** A weighted economics entry — one real org (weight 1) or one profile group (weight = customer count). */
export interface WeightedEconomics {
  econ: OrgEconomics;
  /** How many customers this entry represents. */
  weight: number;
}

/** Blended totals across many customers. Margins are recomputed from the sums, never averaged. */
export interface EconomicsTotals {
  customers: number;
  cost: number;
  overage: number;
  subscription: number;
  cardSpread: number;
  achSpread: number;
  paymentSpread: number;
  totalRev: number;
  totalProfit: number;
  /** null when there's nothing to divide by. */
  subMargin: number | null;
  totalMargin: number | null;
}

/**
 * Aggregate weighted per-customer economics into platform totals. Dollar figures
 * scale by weight; margins are recomputed from the summed dollars so a scenario
 * of 10 identical customers reports the same margin as one of them.
 */
export function aggregateEconomics(entries: WeightedEconomics[]): EconomicsTotals {
  const totals = entries.reduce<EconomicsTotals>(
    (acc, { econ, weight }) => ({
      customers: acc.customers + weight,
      cost: acc.cost + econ.cost * weight,
      overage: acc.overage + econ.overage * weight,
      subscription: acc.subscription + econ.subscription * weight,
      cardSpread: acc.cardSpread + econ.card.spread * weight,
      achSpread: acc.achSpread + econ.ach.spread * weight,
      paymentSpread: acc.paymentSpread + econ.paymentSpread * weight,
      totalRev: acc.totalRev + econ.totalRev * weight,
      totalProfit: acc.totalProfit + econ.totalProfit * weight,
      subMargin: null,
      totalMargin: null,
    }),
    {
      customers: 0,
      cost: 0,
      overage: 0,
      subscription: 0,
      cardSpread: 0,
      achSpread: 0,
      paymentSpread: 0,
      totalRev: 0,
      totalProfit: 0,
      subMargin: null,
      totalMargin: null,
    },
  );

  totals.subMargin =
    totals.subscription > 0 ? (totals.subscription - totals.cost) / totals.subscription : null;
  totals.totalMargin = totals.totalRev > 0 ? totals.totalProfit / totals.totalRev : null;

  return totals;
}
